import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import SlideShell from "../SlideShell";

export default function BirthdateSlide({
  title = "What's your birthdate?",
  subtitle = "We'll calculate your age from this.",
  currentStep,
  totalSteps,
  onNext,
  onBack,
  initialValue = "",
  minAge = 16,
  maxAge = 90,
  nextLabel = "Continue",
  animationDirection = "forward",
  isTransitioning = false,
  autoFocus = true,
  onAutoSave = null,
}) {
  const [birthdate, setBirthdate] = useState(initialValue);
  const [touched, setTouched] = useState(false);
  const inputRef = useRef(null);
  const lastCommittedRef = useRef(JSON.stringify({ birthdateIso8601: initialValue || null, calculatedAgeInYears: null }));

  useEffect(() => {
    setBirthdate(initialValue);
    lastCommittedRef.current = JSON.stringify({ birthdateIso8601: initialValue || null, calculatedAgeInYears: null });
  }, [initialValue]);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  const calcAge = useCallback((isoDate) => {
    if (!isoDate) return null;
    const today = new Date();
    const [y, m, d] = isoDate.split("-").map(Number);
    const dob = new Date(y, (m || 1) - 1, d || 1);
    if (Number.isNaN(dob.getTime())) return null;

    let age = today.getFullYear() - dob.getFullYear();
    const hasHadBirthdayThisYear =
      today.getMonth() > dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());
    if (!hasHadBirthdayThisYear) age -= 1;
    return age;
  }, []);

  const age = useMemo(() => calcAge(birthdate), [birthdate, calcAge]);

  const ageValid =
    typeof age === "number" && age >= minAge && age <= maxAge;

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  // Use today's date as max to ensure pickers open on current year/month on all platforms.
  // Age gating is enforced via validation logic below (ageValid), not by the max attribute.
  const maxDate = `${yyyy}-${mm}-${dd}`;
  const minDate = `${yyyy - maxAge}-${mm}-${dd}`;

  const commitAutoSave = useCallback((dateValue, ageValue) => {
    if (!onAutoSave) return;
    const payload = {
      birthdateIso8601: dateValue || null,
      calculatedAgeInYears: typeof ageValue === 'number' ? ageValue : null,
    };
    const serialized = JSON.stringify(payload);
    if (lastCommittedRef.current === serialized) return;
    lastCommittedRef.current = serialized;
    onAutoSave(payload);
  }, [onAutoSave]);

  useEffect(() => {
    if (!onAutoSave) return;
    if (!birthdate) {
      commitAutoSave(null, null);
      return;
    }
    if (!ageValid) return;
    commitAutoSave(birthdate, age);
  }, [birthdate, age, ageValid, commitAutoSave, onAutoSave]);

  const handleContinue = () => {
    if (!ageValid) return;
    commitAutoSave(birthdate, age);
    onNext({ birthdateIso8601: birthdate, calculatedAgeInYears: age });
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && ageValid) {
      e.preventDefault();
      handleContinue();
    }
  };

  return (
    <SlideShell
      title={title}
      subtitle={subtitle}
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={handleContinue}
      onBack={onBack}
      nextLabel={nextLabel}
      disabled={!ageValid}
      animationDirection={animationDirection}
      isTransitioning={isTransitioning}
    >
      {/* iOS-specific input fixes */}
      <style>{`
        /* Make iOS respect our border, radius, bg and remove inner shadows */
        input[type="date"] {
          -webkit-appearance: none;
          appearance: none;
          background-clip: padding-box;
        }
        /* Align the date text properly on iOS */
        input[type="date"]::-webkit-date-and-time-value {
          text-align: left;
          min-height: 1.25rem; /* ensure value area has height */
          padding-left: 0.125rem; /* small left inset so text isn't clipped */
        }
        /* Ensure calendar icon respects height; some iOS builds need this */
        input[type="date"]::-webkit-calendar-picker-indicator {
          cursor: pointer;
        }
      `}</style>
      <div className="w-full mx-auto">
        <label htmlFor="birthdate" className="block text-sm font-medium text-gray-700 mb-2">
          Birthdate
        </label>
        <input
          id="birthdate"
          ref={inputRef}
          type="date"
          value={birthdate}
          min={minDate}
          max={maxDate}
          onChange={(e) => {
            setBirthdate(e.target.value);
            setTouched(true);
          }}
          onBlur={() => setTouched(true)}
          onKeyDown={onKeyDown}
          inputMode="numeric"
          autoComplete="bday"
          aria-label="Birthdate"
          className={`
            w-full rounded-xl border px-4 py-3 text-[16px] leading-tight h-12
            bg-white appearance-none
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            ${!touched || ageValid ? "border-gray-300" : "border-red-400"}
          `}
        />

      </div>
    </SlideShell>
  );
}
